import React, { useRef } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const utahData = "/img/headerlogo.png";
const bodex = "/img/bodexLogo.png";
const kpi = "/img/KPI-DASH 2.png";
const pdf = "/img/pdf1.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

export default function TransportationTable14({ data }) {
  const pdfRef = useRef();

  if (!data || data.length === 0) {
    return <p>No data available</p>;
  }

  /* =============================
     YEAR EXTRACTION
  ============================== */

  const years = Object.keys(data[0])
    .filter((k) => !isNaN(k) && Number(k) <= 2024)
    .sort();

  const latestYear = years[years.length - 1];

  /* =============================
     SUMMARY CALCULATIONS
  ============================== */

  const summary = data.map((record) => {
    const first = Number(record[years[0]] || 0);
    const last = Number(record[latestYear] || 0);

    const growth = first > 0 ? (((last - first) / first) * 100).toFixed(1) : 0;

    return {
      sex: record.sex,
      latest: last,
      growth,
    };
  });

  const female = summary.find((x) => x.sex === "Female");
  const male = summary.find((x) => x.sex === "Male");

  const genderGap =
    female && male
      ? (male.latest - female.latest).toLocaleString("en-US")
      : "N/A";

  const totalLatest = summary.reduce((sum, item) => sum + item.latest, 0);

  /* =============================
     CHART DATA
  ============================== */

  const chartData = {
    labels: years,
    datasets: data.map((record) => ({
      label: record.sex,
      data: years.map((year) => Number(record[year] || 0)),
      borderColor: record.sex === "Female" ? "#ec4899" : "#2563eb",
      backgroundColor:
        record.sex === "Female"
          ? "rgba(236,72,153,0.15)"
          : "rgba(37,99,235,0.15)",
      tension: 0.3,
      pointRadius: 4,
      fill: false,
    })),
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "🎓 Education Employment by Gender (2018–2024)",
        font: { size: 16, weight: "bold" },
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            return (
              context.dataset.label +
              ": " +
              context.parsed.y.toLocaleString("en-US")
            );
          },
        },
      },
    },
    scales: {
      x: {
        title: { display: true, text: "Year" },
        grid: { display: false },
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Employment Count",
        },
        ticks: {
          callback: (value) => value.toLocaleString("en-US"),
        },
      },
    },
  };

  /* =============================
     PDF EXPORT
  ============================== */

  const downloadPDF = () => {
    const input = pdfRef.current;

    html2canvas(input, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdfDoc = new jsPDF("p", "mm", "a4");

      const pageWidth = pdfDoc.internal.pageSize.getWidth();
      const imgWidth = pageWidth - 10;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      pdfDoc.addImage(utahData, "PNG", 60, 10, 32, 16);
      pdfDoc.setFontSize(26);
      pdfDoc.text("UTahData.org", 95, 22);
      pdfDoc.line(10, 35, 200, 35);

      pdfDoc.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);

      pdfDoc.line(10, 260, 200, 260);
      pdfDoc.addImage(utahData, "PNG", 22, 265, 30, 15);
      pdfDoc.setFontSize(10);
      pdfDoc.text("Built by", 55, 275);
      pdfDoc.addImage(bodex, "PNG", 68, 269, 44, 10);
      pdfDoc.text("using", 112, 275);
      pdfDoc.addImage(kpi, "PNG", 125, 265, 20, 20);
      pdfDoc.text(
        `Downloaded on: ${new Date().toLocaleDateString()}`,
        150,
        295,
      );

      pdfDoc.save("education_gender_trend.pdf");
    });
  };

  /* =============================
     UI
  ============================== */

  return (
    <>
      <div ref={pdfRef} className="p-6 bg-white rounded-xl shadow-lg">
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Education Employment by Gender
        </h2>

        {/* SUMMARY CARDS */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
          {summary.map((item, idx) => (
            <div
              key={idx}
              className={`p-4 rounded-xl shadow ${
                item.sex === "Female" ? "bg-pink-100" : "bg-blue-100"
              }`}
            >
              <p className="text-sm text-gray-600 font-medium">
                {item.sex} ({latestYear})
              </p>
              <p className="text-xl font-bold">
                {item.latest.toLocaleString("en-US")}
              </p>
              <p className="text-sm font-semibold mt-1">
                Growth: +{item.growth}%
              </p>
            </div>
          ))}

          <div className="p-4 bg-purple-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Gender Gap ({latestYear})
            </p>
            <p className="text-xl font-bold text-purple-800">{genderGap}</p>
          </div>
        </div>

        {/* TABLE + CHART */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* TABLE */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm text-center shadow-md rounded-lg overflow-hidden">
              <thead className="bg-gray-800 text-white">
                <tr>
                  <th className="p-2">Year</th>
                  {data.map((record, idx) => (
                    <th key={idx} className="p-2">
                      {record.sex}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {years.map((year) => (
                  <tr
                    key={year}
                    className={`${
                      year === latestYear
                        ? "bg-green-100 font-bold"
                        : "hover:bg-gray-100"
                    }`}
                  >
                    <td className="p-2">{year}</td>
                    {data.map((record, idx) => (
                      <td key={idx} className="p-2">
                        {Number(record[year] || 0).toLocaleString("en-US")}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* CHART */}
          <div className="w-full h-[450px] bg-white rounded-lg shadow-md p-4">
            <Line data={chartData} options={options} />
          </div>
        </div>
      </div>

      {/* DOWNLOAD BUTTON */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdf} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
}
